import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCallback } from 'react';

/**
 * Custom navigation hook that automatically preserves current URL parameters
 * when navigating within the helpdesk module.
 * 
 * This ensures filter state is maintained when navigating between pages.
 */
export const useNavigateWithFilters = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const navigateWithFilters = useCallback((
    to: string,
    options?: {
      replace?: boolean;
      state?: any;
      preserveParams?: boolean; // Allow opt-out if needed
      additionalParams?: Record<string, string>; // Allow additional params
    }
  ) => {
    const { 
      replace = false, 
      state, 
      preserveParams = true, 
      additionalParams = {} 
    } = options || {};

    if (!preserveParams) {
      // Navigate without preserving parameters if explicitly disabled
      navigate(to, { replace, state });
      return;
    }

    // Preserve current search parameters
    const currentParams = new URLSearchParams(searchParams);
    
    // Add any additional parameters
    Object.entries(additionalParams).forEach(([key, value]) => {
      currentParams.set(key, value);
    });

    const queryString = currentParams.toString();
    const finalUrl = queryString ? `${to}?${queryString}` : to;
    
    console.log(`🧭 Navigating with preserved filters: ${finalUrl}`);
    navigate(finalUrl, { replace, state });
  }, [navigate, searchParams]);

  return navigateWithFilters;
};
